import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { IMAGES, waLink } from "../lib/site";
import { FadeUp } from "./Reveal";

const VIBES = [
    {
        id: "terrazzo-pop",
        roman: "I",
        name: "Terrazzo Pop",
        desc: "Playful floors, fluted wood, matte black",
        img: IMAGES.vibe_terrazzo,
    },
    {
        id: "noir-marble",
        roman: "II",
        name: "Noir Marble",
        desc: "Grey marble, black fittings, hotel-dark luxe",
        img: IMAGES.vibe_noir,
    },
    {
        id: "heritage-green",
        roman: "III",
        name: "Heritage Green",
        desc: "Kit-kat tiles, warm teak, brass everywhere",
        img: IMAGES.vibe_green,
    },
    {
        id: "warm-minimal",
        roman: "IV",
        name: "Warm Minimal",
        desc: "Calm stone, clear glass, soft evening light",
        img: IMAGES.vibe_warm,
    },
];

const VibePicker = () => {
    const [idx, setIdx] = useState(0);
    const active = VIBES[idx];

    const href = waLink(
        `Hi Bath & Beyond! My vibe is ${active.name} — can my bathroom look like this?`
    );

    return (
        <section
            className="relative bg-noir text-white overflow-hidden min-h-[100svh] flex flex-col"
            data-testid="vibe-section"
        >
            <div className="spotlight" />
            <div className="relative flex flex-col flex-1 mx-auto w-full max-w-[1440px] px-5 sm:px-10 pt-24 sm:pt-28 pb-5">
                <div className="text-center md:hidden">
                    <FadeUp>
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-brass mb-3">
                            One bathroom. Four futures.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.08}>
                        <h2 className="font-serif font-light leading-[1.02] tracking-tight text-[clamp(1.9rem,4vw,3.4rem)]">
                            <span className="block uppercase italic text-brass text-[1.3em] leading-none tracking-wide">
                                Transform
                            </span>
                            <span className="block mt-1.5">your bathroom.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.14}>
                        <p className="mt-2.5 text-[11px] sm:text-sm font-light text-white/50">
                            The same bathroom, four ways &mdash; click through to picture yours.
                        </p>
                    </FadeUp>
                </div>

                <div className="relative flex-1 flex flex-col md:flex-row items-center justify-center gap-5 md:gap-10 mt-3 min-h-[420px]">
                    <figure
                        className="md:hidden relative overflow-hidden rounded-[1.5rem] shadow-[0_30px_80px_rgba(0,0,0,0.5)] ring-1 ring-brass/30 h-[24vh] aspect-[4/5]"
                        data-testid="vibe-original-mobile"
                    >
                        <img
                            src={IMAGES.vibe_original}
                            alt="Original bathroom before renovation"
                            loading="lazy"
                            className="h-full w-full object-cover img-treated"
                        />
                        <p className="font-mono text-[8px] sm:text-[9px] tracking-[0.25em] uppercase text-white/60 text-center">
                            How you&rsquo;re living &mdash; today
                        </p>
                    </figure>

                    <div className="hidden md:flex flex-col items-center gap-4 w-[16rem] lg:w-[17rem] shrink-0">
                        <div className="text-center">
                            <FadeUp>
                                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-brass mb-2.5">
                                    One bathroom. Four futures.
                                </p>
                            </FadeUp>
                            <FadeUp delay={0.08}>
                                <h2 className="font-serif font-light leading-[1.02] tracking-tight text-[clamp(1.7rem,2.4vw,2.4rem)]">
                                    <span className="block uppercase italic text-brass text-[1.3em] leading-none tracking-wide">
                                        Transform
                                    </span>
                                    <span className="block mt-1.5">your bathroom.</span>
                                </h2>
                            </FadeUp>
                            <FadeUp delay={0.14}>
                                <p className="mt-2 text-[11px] font-light leading-relaxed text-white/50">
                                    The same bathroom, four ways &mdash; click through to picture yours.
                                </p>
                            </FadeUp>
                        </div>
                        <figure
                            className="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.5)] ring-1 ring-brass/30 h-[36vh] max-h-[380px] aspect-[4/5]"
                            data-testid="vibe-original"
                        >
                            <img
                                src={IMAGES.vibe_original}
                                alt="Original bathroom before renovation"
                                loading="lazy"
                                className="h-full w-full object-cover img-treated"
                            />
                        </figure>
                        <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/60 text-center">
                            How you&rsquo;re living &mdash; today
                        </p>
                    </div>

                    <div className="relative h-12 md:h-[26vh] w-12 md:w-24 shrink-0" aria-hidden="true" data-testid="vibe-arrow">
                        <svg className="md:hidden h-full w-full" viewBox="0 0 40 100" preserveAspectRatio="none">
                            <path
                                d="M 20 2 C 14 30, 14 64, 19 94"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                                className="text-brass dash-flow"
                                stroke="currentColor"
                                strokeWidth="1.6"
                            />
                        </svg>
                        <svg className="hidden md:block h-full w-full" viewBox="0 0 100 40" preserveAspectRatio="none">
                            <path
                                d="M 2 24 C 30 14, 66 12, 94 18"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                                className="text-brass dash-flow"
                                stroke="currentColor"
                                strokeWidth="1.6"
                            />
                        </svg>
                        <ChevronDown className="md:hidden absolute bottom-[-6px] left-1/2 -translate-x-1/2 h-5 w-5 text-brass" />
                        <ChevronRight className="hidden md:block absolute right-[-8px] top-1/2 -translate-y-1/2 h-6 w-6 text-brass" />
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                        <figure
                            className="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.55)] h-[32vh] md:h-[68vh] max-h-[620px] aspect-[4/5]"
                            data-testid="vibe-style-image"
                        >
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={active.id}
                                    src={active.img}
                                    alt={`${active.name} bathroom transformation`}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover img-treated"
                                    initial={{ opacity: 0, x: 30, scale: 1.04 }}
                                    animate={{ opacity: 1, x: 0, scale: 1 }}
                                    exit={{ opacity: 0, x: -30 }}
                                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                                />
                            </AnimatePresence>
                        </figure>

                        <div className="flex flex-col items-center md:items-start gap-3 md:w-[15rem] lg:w-[17rem] text-center md:text-left" data-testid="vibe-text">
                            <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-white/55">
                                How you could be living &mdash; tomorrow
                            </p>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active.id}
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -14 }}
                                    transition={{ duration: 0.35 }}
                                >
                                    <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-brass mb-2">
                                        Vibe {active.roman}
                                    </p>
                                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
                                        {active.name}
                                    </h3>
                                    <p className="mt-2 text-[11px] sm:text-xs lg:text-sm font-light text-white/65">
                                        {active.desc}
                                    </p>
                                </motion.div>
                            </AnimatePresence>

                            <button
                                data-testid="vibe-next-btn"
                                onClick={() => setIdx((i) => (i + 1) % VIBES.length)}
                                className="mt-1 flex items-center gap-2 rounded-full border border-brass/50 px-5 py-2.5 text-xs sm:text-sm font-medium text-brass transition-all duration-300 hover:bg-brass hover:text-noir"
                            >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Show another style
                            </button>
                            <div className="flex items-center gap-1.5" data-testid="vibe-controls">
                                {VIBES.map((v, i) => (
                                    <button
                                        key={v.id}
                                        data-testid={`vibe-dot-${i}`}
                                        onClick={() => setIdx(i)}
                                        aria-label={`Show ${v.name}`}
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            i === idx ? "w-6 bg-brass" : "w-2 bg-white/25 hover:bg-white/50"
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <FadeUp delay={0.15}>
                    <div className="mt-3 flex flex-col items-center gap-1.5 text-center">
                        <a
                            data-testid="vibe-whatsapp-cta"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-full bg-brass px-7 py-3 sm:px-10 sm:py-3.5 text-xs sm:text-sm font-semibold text-noir transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
                        >
                            I love the {active.name} vibe &mdash; renovate mine
                        </a>
                        <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/40">
                            A Spaces by Samita venture &mdash; click through the styles, we&rsquo;ll renovate yours in the one you love
                        </p>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};

export default VibePicker;
